import { getDayNumber, getWeekdayShort } from '@/lib/date';

export interface LessonRow {
  id: string;
  title: string;
  group_id: string | null;
  level: string | null;
  subject: string | null;
  date: string | null;
  start_time: string | null;
  end_time: string | null;
  type: string | null;
  live_link: string | null;
  description: string | null;
  status: string;
  has_record: boolean;
  has_document: boolean;
  has_assignment: boolean;
}

export interface GroupInfo {
  name: string;
  color: string;
}

interface WeekCalendarProps {
  weekDays: Date[];
  lessonsByDate: Record<string, LessonRow[]>;
  groupMap: Record<string, GroupInfo>;
  todayYmd: string;
  onSelectLesson: (lesson: LessonRow) => void;
}

function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default function WeekCalendar({
  weekDays,
  lessonsByDate,
  groupMap,
  todayYmd,
  onSelectLesson,
}: WeekCalendarProps) {
  return (
    <div className="overflow-x-auto">
      <div className="grid min-w-[900px] grid-cols-7 gap-3">
        {weekDays.map((day) => {
          const ymd = toYmd(day);
          const isToday = ymd === todayYmd;
          const dayLessons = (lessonsByDate[ymd] ?? []).slice().sort((a, b) =>
            (a.start_time ?? '').localeCompare(b.start_time ?? ''),
          );
          const visible = dayLessons.slice(0, 3);
          const hiddenCount = dayLessons.length - visible.length;

          return (
            <div
              key={ymd}
              className={`flex flex-col rounded-xl border ${
                isToday ? 'border-primary-300 bg-primary-50/50' : 'border-background-200 bg-white'
              }`}
            >
              <div
                className={`flex flex-col items-center gap-0.5 rounded-t-xl border-b px-2 py-3 ${
                  isToday ? 'border-primary-200 bg-primary-100/60' : 'border-background-100 bg-background-50'
                }`}
              >
                <span className="text-xs font-semibold capitalize text-foreground-500">
                  {getWeekdayShort(ymd)}
                </span>
                <span
                  className={`flex h-8 w-8 items-center justify-center rounded-full font-heading text-sm font-bold ${
                    isToday ? 'bg-primary-500 text-white' : 'text-foreground-800'
                  }`}
                >
                  {getDayNumber(ymd)}
                </span>
              </div>

              <div className="flex flex-1 flex-col gap-2 p-2">
                {dayLessons.length === 0 ? (
                  <div className="flex flex-1 items-center justify-center py-8 text-xs text-foreground-400">
                    Ders yok
                  </div>
                ) : (
                  <>
                    {visible.map((lesson) => {
                      const group = lesson.group_id ? groupMap[lesson.group_id] : undefined;
                      return (
                        <button
                          key={lesson.id}
                          type="button"
                          onClick={() => onSelectLesson(lesson)}
                          className="w-full rounded-lg border border-background-200 bg-white p-2 text-left transition hover:border-background-300 hover:bg-background-50 cursor-pointer"
                          style={{
                            borderLeft: `3px solid ${group?.color ?? '#cbd5e1'}`,
                          }}
                        >
                          <p className="text-xs font-semibold text-foreground-800">
                            {lesson.start_time ? lesson.start_time.slice(0, 5) : '--:--'}
                          </p>
                          <p className="mt-0.5 line-clamp-2 text-xs font-medium text-foreground-950">
                            {lesson.title}
                          </p>
                          {group && (
                            <p className="mt-0.5 truncate text-[11px] text-foreground-500">{group.name}</p>
                          )}
                          {lesson.live_link && (
                            <span className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600">
                              <i className="ri-vidicon-line" /> Canlı
                            </span>
                          )}
                        </button>
                      );
                    })}
                    {hiddenCount > 0 && (
                      <button
                        type="button"
                        onClick={() => onSelectLesson(dayLessons[3])}
                        className="w-full rounded-lg px-2 py-1 text-center text-xs font-semibold text-primary-600 transition hover:bg-primary-50 cursor-pointer"
                      >
                        +{hiddenCount} ders daha
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}